import React from 'react';
import { MdClose, MdAdd, MdDeleteOutline } from 'react-icons/md';
import { EnhancedUser } from 'types/profile.types';
import { updateUserPreferences } from '@services/cloudFunctions';
import notificationService from '@services/notificationService';

interface UserPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: EnhancedUser | null;
  tenantId?: string | null;
  onSaved?: (uid: string) => Promise<void> | void;
}

interface PrefRow {
  key: string;
  value: string;
}

// Keep in sync with normalizePreferences in functions/management.js
const MAX_PAIRS = 20;
const MAX_KEY = 50;
const MAX_VALUE = 200;

const UserPreferencesModal: React.FC<UserPreferencesModalProps> = ({ isOpen, onClose, user, tenantId, onSaved }) => {
  const [rows, setRows] = React.useState<PrefRow[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);

  // Seed rows from the user's existing preferences each time the modal opens
  React.useEffect(() => {
    if (isOpen && user) {
      const prefs = user.profile?.customPreferences || {};
      const seeded = Object.entries(prefs).map(([key, value]) => ({ key, value: String(value) }));
      setRows(seeded.length > 0 ? seeded : [{ key: '', value: '' }]);
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const updateRow = (index: number, field: keyof PrefRow, val: string) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: val } : r)));
  };

  const addRow = () => {
    setRows((prev) => (prev.length >= MAX_PAIRS ? prev : [...prev, { key: '', value: '' }]));
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // Serialize rows -> object: trim, drop empty key/value, last-wins on duplicate keys
    const preferences: Record<string, string> = {};
    for (const r of rows) {
      const key = r.key.trim().slice(0, MAX_KEY);
      const value = r.value.trim().slice(0, MAX_VALUE);
      if (key && value) preferences[key] = value;
    }

    setIsSaving(true);
    try {
      const result = await updateUserPreferences(user.uid, preferences, tenantId);
      if (result.success) {
        notificationService.success('Preferences saved');
        if (onSaved) await onSaved(user.uid);
        onClose();
      } else {
        notificationService.error(result.error || 'Failed to save preferences');
      }
    } catch (error: any) {
      notificationService.error(error.message || 'Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-navy-800 rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <div>
            <h2 className="text-xl font-bold text-navy-700 dark:text-white">Preferences</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{user.displayName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-lg transition-colors"
          >
            <MdClose className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Add custom key/value preferences for this user (e.g.{' '}
            <span className="font-medium">Meeting format</span> &rarr;{' '}
            <span className="font-medium">Virtual</span>). These are used as a soft signal when
            generating matches.
          </p>

          {/* Column labels */}
          <div className="hidden sm:flex gap-3 mb-2 px-1">
            <span className="flex-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Preference
            </span>
            <span className="flex-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Value
            </span>
            <span className="w-9" />
          </div>

          <div className="space-y-2">
            {rows.map((row, index) => (
              <div key={index} className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <input
                  type="text"
                  value={row.key}
                  maxLength={MAX_KEY}
                  onChange={(e) => updateRow(index, 'key', e.target.value)}
                  placeholder="e.g. Meeting format"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
                <input
                  type="text"
                  value={row.value}
                  maxLength={MAX_VALUE}
                  onChange={(e) => updateRow(index, 'value', e.target.value)}
                  placeholder="e.g. Virtual"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-navy-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
                <button
                  onClick={() => removeRow(index)}
                  aria-label="Remove preference"
                  className="flex items-center justify-center w-9 h-9 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                >
                  <MdDeleteOutline className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>

          {rows.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic py-2">No preferences yet.</p>
          )}

          <button
            onClick={addRow}
            disabled={rows.length >= MAX_PAIRS}
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MdAdd className="h-4 w-4" />
            Add preference
          </button>
          {rows.length >= MAX_PAIRS && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Maximum of {MAX_PAIRS} preferences.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-navy-700 rounded-lg hover:bg-gray-200 dark:hover:bg-navy-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {isSaving && (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            )}
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPreferencesModal;
